import { useMemo } from 'react';
import { Car, ArrowLeftRight, CheckCircle2 } from 'lucide-react';
import { MODELS_BY_MAKE } from '../../superadmin/data/makeModelTrimData';
import { US_STATES } from '../../data/usStates';
import AddressAutocompleteInput from './AddressAutocompleteInput';
import '../AllCss/AddressAutocompleteInput.css';

const CAR_MAKES = Object.keys(MODELS_BY_MAKE).sort((a, b) => a.localeCompare(b));

const YEAR_OPTIONS = Array.from(
  { length: new Date().getFullYear() + 2 - 1990 },
  (_, i) => new Date().getFullYear() + 1 - i
);

const INTEREST_TYPE_OPTIONS = [
  {
    value: 'purchase',
    icon: Car,
    title: 'Buy a vehicle',
    description: 'Shop our lot or describe what you want.',
  },
  {
    value: 'sell',
    icon: ArrowLeftRight,
    title: 'Sell or trade',
    description: 'Tell us about your current vehicle.',
  },
];

function normalizeDollarWholeInput(raw) {
  return String(raw ?? '').replace(/\D/g, '').slice(0, 9);
}

function formatDollarWholeDisplay(stored) {
  if (stored === '' || stored == null) return '';
  const d = String(stored).replace(/\D/g, '');
  if (!d) return '';
  return Number(d).toLocaleString('en-US', { maximumFractionDigits: 0 });
}

export default function RegisterInterestStep({
  interestType,
  onInterestTypeChange,
  address,
  onAddressChange,
  interest,
  onInterestChange,
  loading,
  onSubmit,
  onSkip,
}) {
  const interestModelOptions = useMemo(() => {
    if (!interest.interestMake || !MODELS_BY_MAKE[interest.interestMake]) return [];
    return MODELS_BY_MAKE[interest.interestMake];
  }, [interest.interestMake]);

  const sellModelOptions = useMemo(() => {
    if (!interest.sellMake || !MODELS_BY_MAKE[interest.sellMake]) return [];
    return MODELS_BY_MAKE[interest.sellMake];
  }, [interest.sellMake]);

  const handleField = (e) => {
    const { name, value } = e.target;
    onInterestChange((prev) => ({
      ...prev,
      [name]: name === 'budgetMax' ? normalizeDollarWholeInput(value) : value,
    }));
  };

  const handleAddressField = (e) => {
    const { name, value } = e.target;
    onAddressChange((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <form onSubmit={onSubmit} className="auth-form auth-form-details">
      <div className="auth-interest-recommend-banner" role="note">
        <strong>Highly recommended.</strong> This helps us match you with vehicles on our lot and send
        you relevant follow-ups. You can complete it later from your profile.
      </div>
      <p className="auth-section-label">Your address</p>
      <div className="auth-row auth-row-address auth-address-row">
        <AddressAutocompleteInput
          id="reg-address-autocomplete"
          label="Street address"
          value={address}
          onChange={(patch) => onAddressChange((prev) => ({ ...prev, ...patch }))}
          includeCounty={false}
          disabled={loading}
          labelClassName="auth-label"
          inputClassName="auth-input"
          placeholder="Start typing your address…"
          className="auth-field auth-address-autocomplete"
        />
        <div className="auth-field auth-address-apt">
          <label className="auth-label" htmlFor="reg-address2">
            Apt / suite
          </label>
          <input
            id="reg-address2"
            name="address2"
            type="text"
            value={address.address2}
            onChange={handleAddressField}
            className="auth-input"
            disabled={loading}
          />
        </div>
      </div>
      <div className="auth-row auth-row-3">
        <div className="auth-field">
          <label className="auth-label" htmlFor="reg-city">
            City
          </label>
          <input
            id="reg-city"
            name="city"
            type="text"
            value={address.city}
            onChange={handleAddressField}
            className="auth-input"
            disabled={loading}
          />
        </div>
        <div className="auth-field">
          <label className="auth-label" htmlFor="reg-state">
            State
          </label>
          <select
            id="reg-state"
            name="state"
            value={address.state}
            onChange={handleAddressField}
            className="auth-input auth-select"
            disabled={loading}
          >
            <option value="">State</option>
            {US_STATES.map(([code, name]) => (
              <option key={code} value={code}>
                {name}
              </option>
            ))}
          </select>
        </div>
        <div className="auth-field">
          <label className="auth-label" htmlFor="reg-zip">
            ZIP
          </label>
          <input
            id="reg-zip"
            name="zipCode"
            type="text"
            value={address.zipCode}
            onChange={handleAddressField}
            className="auth-input"
            maxLength={10}
            disabled={loading}
          />
        </div>
      </div>

      <p className="auth-section-label">What are you interested in?</p>
      <div className="auth-interest-cards">
        {INTEREST_TYPE_OPTIONS.map((opt) => {
          const Icon = opt.icon;
          const selected = interestType === opt.value;
          return (
            <label
              key={opt.value}
              className={`auth-interest-card${selected ? ' auth-interest-card--selected' : ''}`}
            >
              <input
                type="radio"
                name="interestType"
                value={opt.value}
                checked={selected}
                onChange={() => onInterestTypeChange(opt.value)}
                className="auth-interest-input"
                disabled={loading}
              />
              <span className="auth-interest-icon" aria-hidden>
                <Icon size={20} strokeWidth={1.75} />
              </span>
              <span className="auth-interest-text">
                <span className="auth-interest-title">{opt.title}</span>
                <span className="auth-interest-desc">{opt.description}</span>
              </span>
              <span className="auth-interest-check" aria-hidden>
                {selected && <CheckCircle2 size={16} />}
              </span>
            </label>
          );
        })}
      </div>

      {!interestType && (
        <p className="auth-interest-prompt">Select buy or sell above to show the next fields.</p>
      )}

      {interestType === 'purchase' && (
        <>
          <p className="auth-section-label">What you&apos;re looking for</p>
          <div className="auth-row auth-row-4">
            <div className="auth-field">
              <label className="auth-label" htmlFor="interestMake">
                Make
              </label>
              <select
                id="interestMake"
                name="interestMake"
                value={interest.interestMake}
                onChange={handleField}
                className="auth-input auth-select"
                disabled={loading}
              >
                <option value="">Any</option>
                {CAR_MAKES.map((make) => (
                  <option key={make} value={make}>
                    {make}
                  </option>
                ))}
              </select>
            </div>
            <div className="auth-field">
              <label className="auth-label" htmlFor="interestModel">
                Model
              </label>
              <select
                id="interestModel"
                name="interestModel"
                value={interest.interestModel}
                onChange={handleField}
                className="auth-input auth-select"
                disabled={loading || !interest.interestMake}
              >
                <option value="">{interest.interestMake ? 'Any' : 'Make first'}</option>
                {interestModelOptions.map((model) => (
                  <option key={model} value={model}>
                    {model}
                  </option>
                ))}
              </select>
            </div>
            <div className="auth-field">
              <label className="auth-label" htmlFor="interestYearMin">
                Year from
              </label>
              <select
                id="interestYearMin"
                name="interestYearMin"
                value={interest.interestYearMin}
                onChange={handleField}
                className="auth-input auth-select"
                disabled={loading}
              >
                <option value="">Any</option>
                {YEAR_OPTIONS.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
            <div className="auth-field">
              <label className="auth-label" htmlFor="interestYearMax">
                Year to
              </label>
              <select
                id="interestYearMax"
                name="interestYearMax"
                value={interest.interestYearMax}
                onChange={handleField}
                className="auth-input auth-select"
                disabled={loading}
              >
                <option value="">Any</option>
                {YEAR_OPTIONS.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="auth-row auth-row-2">
            <div className="auth-field">
              <label className="auth-label" htmlFor="interestMileageMax">
                Max mileage
              </label>
              <input
                id="interestMileageMax"
                name="interestMileageMax"
                type="number"
                min="0"
                value={interest.interestMileageMax}
                onChange={handleField}
                className="auth-input"
                placeholder="80000"
                disabled={loading}
              />
            </div>
            <div className="auth-field">
              <label className="auth-label" htmlFor="budgetMax">
                Max budget
              </label>
              <div className="auth-currency">
                <span>$</span>
                <input
                  id="budgetMax"
                  name="budgetMax"
                  type="text"
                  inputMode="numeric"
                  value={formatDollarWholeDisplay(interest.budgetMax)}
                  onChange={handleField}
                  className="auth-input auth-input-currency"
                  placeholder="25,000"
                  disabled={loading}
                />
              </div>
            </div>
          </div>
        </>
      )}

      {interestType === 'sell' && (
        <>
          <p className="auth-section-label">Your vehicle</p>
          <div className="auth-row auth-row-3 auth-row-sell-mmy">
            <div className="auth-field">
              <label className="auth-label" htmlFor="sellMake">
                Make
              </label>
              <select
                id="sellMake"
                name="sellMake"
                value={interest.sellMake}
                onChange={(e) => {
                  handleField(e);
                  onInterestChange((prev) => ({ ...prev, sellModel: '' }));
                }}
                className="auth-input auth-select"
                disabled={loading}
              >
                <option value="">Make</option>
                {CAR_MAKES.map((make) => (
                  <option key={make} value={make}>
                    {make}
                  </option>
                ))}
              </select>
            </div>
            <div className="auth-field">
              <label className="auth-label" htmlFor="sellModel">
                Model
              </label>
              <select
                id="sellModel"
                name="sellModel"
                value={interest.sellModel}
                onChange={handleField}
                className="auth-input auth-select"
                disabled={loading || !interest.sellMake}
              >
                <option value="">Model</option>
                {sellModelOptions.map((model) => (
                  <option key={model} value={model}>
                    {model}
                  </option>
                ))}
              </select>
            </div>
            <div className="auth-field">
              <label className="auth-label" htmlFor="sellYear">
                Year
              </label>
              <select
                id="sellYear"
                name="sellYear"
                value={interest.sellYear}
                onChange={handleField}
                className="auth-input auth-select"
                disabled={loading}
              >
                <option value="">Year</option>
                {YEAR_OPTIONS.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="auth-row auth-row-2">
            <div className="auth-field">
              <label className="auth-label" htmlFor="sellMileage">
                Mileage
              </label>
              <input
                id="sellMileage"
                name="sellMileage"
                type="number"
                min="0"
                value={interest.sellMileage}
                onChange={handleField}
                className="auth-input"
                disabled={loading}
              />
            </div>
            <div className="auth-field">
              <label className="auth-label" htmlFor="sellCondition">
                Condition
              </label>
              <select
                id="sellCondition"
                name="sellCondition"
                value={interest.sellCondition}
                onChange={handleField}
                className="auth-input auth-select"
                disabled={loading}
              >
                <option value="used">Used</option>
                <option value="new">New</option>
                <option value="certified">Certified</option>
              </select>
            </div>
          </div>
        </>
      )}

      <div className="auth-interest-actions">
        <button type="submit" className="auth-btn" disabled={loading}>
          {loading ? 'Creating account…' : 'Create account'}
        </button>
        {onSkip && (
          <button
            type="button"
            className="auth-btn auth-btn-skip"
            disabled={loading}
            onClick={onSkip}
          >
            Skip for now
          </button>
        )}
      </div>
    </form>
  );
}
